import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, User } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { UrawaIcon } from '../UrawaIcon';

export function PublicNav() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated, user } = useAuthStore();

  const links = [
    { to: '/#features', label: 'Features' },
    { to: '/user-guide', label: 'User Guide' },
    { to: '/pricing', label: 'Pricing' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-legal-900/95 backdrop-blur-sm border-b border-legal-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-1.5">
            <UrawaIcon className="h-6 w-6 text-accent" />
            <span className="text-lg font-bold text-white tracking-tight">Urava</span>
            <span className="hidden sm:inline text-xs text-legal-400 border border-legal-700 rounded px-1.5 py-0.5 ml-1">
              Legal AI
            </span>
            <span className="text-xs font-semibold text-amber-400 border border-amber-500/40 bg-amber-500/10 rounded px-1.5 py-0.5 ml-1">
              Beta
            </span>
          </Link>

          {/* Desktop Links */}
          <div className="hidden md:flex items-center space-x-1">
            {links.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.to)
                    ? 'text-accent bg-legal-800'
                    : 'text-legal-300 hover:text-white hover:bg-legal-800'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-3">
            {isAuthenticated ? (
              <>
                {user && (
                  <div className="flex items-center space-x-2 text-legal-300 text-sm">
                    <User className="h-4 w-4" />
                    <span>{user.full_name.split(' ')[0]}</span>
                  </div>
                )}
                <Link
                  to="/app"
                  className="text-sm font-semibold bg-accent text-legal-900 px-4 py-2 rounded-lg hover:bg-accent-400 transition-colors"
                >
                  Open app →
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm font-medium text-legal-300 hover:text-white transition-colors px-3 py-2"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="text-sm font-semibold bg-accent text-legal-900 px-4 py-2 rounded-lg hover:bg-accent-400 transition-colors"
                >
                  Start Free →
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu toggle */}
          <button
            className="md:hidden text-legal-300 hover:text-white"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-legal-900 border-t border-legal-800 px-4 py-4 space-y-2">
          {links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMenuOpen(false)}
              className="block px-4 py-3 rounded-lg text-legal-300 hover:text-white hover:bg-legal-800 text-sm font-medium"
            >
              {link.label}
            </Link>
          ))}
          <div className="pt-2 space-y-2 border-t border-legal-800">
            {isAuthenticated ? (
              <>
                {user && (
                  <div className="flex items-center space-x-2 px-4 py-2 text-legal-300 text-sm">
                    <User className="h-4 w-4" />
                    <span>{user.full_name}</span>
                  </div>
                )}
                <Link
                  to="/app"
                  onClick={() => setMenuOpen(false)}
                  className="block px-4 py-3 bg-accent text-legal-900 rounded-lg text-sm font-semibold text-center"
                >
                  Open app →
                </Link>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setMenuOpen(false)} className="block px-4 py-3 text-legal-300 text-sm font-medium">
                  Sign In
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMenuOpen(false)}
                  className="block px-4 py-3 bg-accent text-legal-900 rounded-lg text-sm font-semibold text-center"
                >
                  Start Free →
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
